// Source of truth for Membership Terms & Conditions.
//
// The canonical documents live under /terms/*.docx.
// We keep deterministic extracted artifacts under src/content/terms/* so:
// - the frontend can render the exact Word formatting (tables, lists), and
// - we have a stable plain-text representation for hashing/PDF generation.

import ABRAHAM_TERMS_HTML from '../content/terms/abraham-terms.html?raw';
import ABRAHAM_TERMS_TEXT from '../content/terms/abraham-terms.txt?raw';
// Full, unified terms bundles (text) used for hashing + receipts.
import CCS_TERMS_TEXT from '../content/ccs-unified-terms.txt?raw';
import HALES_TERMS_TEXT from '../content/hales-comfort-club-terms.txt?raw';
import { detectBrandFromHost, type BrandKey } from './brand-utils';

export type TermsBundle = {
  title: string;
  version: string;
  effectiveDate: string;
  text: string;
  html?: string;
};

const CCS_TERMS: TermsBundle = {
  title: 'Comfort Club Membership Terms & Conditions',
  version: '2025-12-27',
  effectiveDate: 'December 27, 2025',
  text: CCS_TERMS_TEXT.trimEnd(),
};

const HALES_TERMS: TermsBundle = {
  title: 'Comfort Club Membership Terms & Conditions',
  version: '2026-01-02',
  effectiveDate: 'January 2, 2026',
  text: HALES_TERMS_TEXT.trimEnd(),
};

const ABRAHAM_TERMS: TermsBundle = {
  title: 'Comfort Club Membership Terms & Conditions',
  version: '2026-01',
  effectiveDate: 'January 2026',
  text: ABRAHAM_TERMS_TEXT.trimEnd(),
  html: ABRAHAM_TERMS_HTML,
};

const TERMS_BY_BRAND: Record<BrandKey, TermsBundle> = {
  climate: CCS_TERMS,
  hales: HALES_TERMS,
  abraham: ABRAHAM_TERMS,
};

function resolveBrand(brand?: BrandKey): BrandKey {
  if (brand) return brand;
  if (typeof window !== 'undefined') return detectBrandFromHost();
  return 'climate';
}

export function getMembershipTerms(brand?: BrandKey): TermsBundle {
  return TERMS_BY_BRAND[resolveBrand(brand)] || CCS_TERMS;
}

export function getMembershipTermsText(brand?: BrandKey): string {
  return getMembershipTerms(brand).text;
}

export function getMembershipTermsHtml(brand?: BrandKey): string | null {
  return getMembershipTerms(brand).html || null;
}

export function getMembershipTermsTitle(brand?: BrandKey): string {
  return getMembershipTerms(brand).title;
}

export function getMembershipTermsVersion(brand?: BrandKey): string {
  return getMembershipTerms(brand).version;
}

export function getMembershipTermsEffectiveDate(brand?: BrandKey): string {
  return getMembershipTerms(brand).effectiveDate;
}

// Backwards-compatible exports (default to Climate/CCS).
export const MEMBERSHIP_TERMS_TEXT = CCS_TERMS.text;
export const MEMBERSHIP_TERMS_VERSION = CCS_TERMS.version;
export const MEMBERSHIP_TERMS_EFFECTIVE_DATE = CCS_TERMS.effectiveDate;
